import { Component, EventEmitter, Input, NgZone, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { asyncScheduler, Subject, takeUntil, throttleTime } from 'rxjs';
import { BLEManagerService } from 'src/app/services/ble/ble-manager.service';
import { IFrequency } from 'src/app/services/ble/IFrequency';
import { IQConnect } from 'src/app/services/ble/IQConnect';
import { IQDeviceStatus } from 'src/app/services/ble/IQDeviceStatus';
import { ISessionLog } from 'src/app/services/ble/ISessionLog';
import { ITreatment } from 'src/app/services/ble/ITreatment';
import { PreloadLevelType } from 'src/app/services/ble/PreloadLevelType';

@Component({
  selector: 'app-iq-connect-device',
  templateUrl: './iq-connect-device.component.html',
  styleUrls: ['./iq-connect-device.component.scss'],
  standalone: false
})

export class IQConnectDeviceComponent implements OnInit, OnDestroy {

  @Input() bodyPart: string | undefined | null = null;  // Input property for binding
  @Input() hidden: boolean = false;  // Input property for binding
  @Input() deviceId: string | null = null;  // Input property for binding
  @Input() deviceSerialNumber: string | null = null;
  @Input() scanOnStartup: boolean = true;
  @Input() onlyRenderDisconnect: boolean = false;

  @Output() deviceChanged = new EventEmitter<IQDeviceStatus | null>(); // Output event to notify changes
  @Output() onTreatment = new EventEmitter<ITreatment>(); // Output event to notify changes
  @Output() onTrigger = new EventEmitter<boolean>(); // Output event to notify changes
  @Output() onDoubleTrigger = new EventEmitter<void>(); // Output event to notify changes
  @Output() onDeviceInformation = new EventEmitter<ISessionLog>(); // Output event to notify changes
  @Output() onFrequency = new EventEmitter<IFrequency[]>(); // Output event to notify changes
  @Output() onConnected = new EventEmitter<boolean>(); //Connected
  @Output() onPreload = new EventEmitter<PreloadLevelType>(); //Connected
  @Output() onForceLevel = new EventEmitter<number>();
  @Output() onSerialNumber = new EventEmitter<string | null>();
  @Output() onDisplayBoardFirmwareVersion = new EventEmitter<string | null>();
  @Output() onMainBoardFirmwareVersion = new EventEmitter<string | null>();
  @Output() onDisconnected = new EventEmitter<boolean>(); //Disconnected

  iqDevice: IQDeviceStatus = {};
  frequency: IFrequency | null = null;
  treatment: ITreatment | null = null;
  debugMessages: string[] = [];
  scanning: boolean = false;
  bleReady: boolean = false;
  connecting: boolean = false;
  serialNumber: string | null = null;

  private destroy$ = new Subject<void>(); // ✅ Used for cleanu

  constructor(private bleService: BLEManagerService,
    private ngZone: NgZone
  ) {
    this.iqDevice.isConnected = IQConnect.getConnected();
  }

  ngOnInit() {
    this.setupBLEDevice();

    // Removed auto-connect logic - user must manually trigger connection via button
    setTimeout(async () => {
      this.onConnected.emit(this.iqDevice.isConnected);
    }, 10);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async setupBLEDevice() {
    this.bleService.onIsConnected()
      .pipe(takeUntil(this.destroy$))
      .subscribe(async (isConnected) => {
        this.ngZone.run(() => {
          this.iqDevice.isConnected = isConnected;
        });
      });

    this.bleService.onInitialized()
      .pipe(takeUntil(this.destroy$))
      .subscribe(initialized => {
        this.ngZone.run(() => {
          this.iqDevice.initialised = initialized;
        });
      });

    this.bleService.onScanning()
      .pipe(takeUntil(this.destroy$))
      .subscribe(scanning => {
        this.ngZone.run(() => {
          this.scanning = scanning;
        });
      });

    this.bleService.onConnected()
      .pipe(takeUntil(this.destroy$))
      .subscribe(deviceId => {
        this.ngZone.run(() => {
          this.iqDevice.deviceId = deviceId;
          this.iqDevice.isConnected = true;
          this.onConnected.emit(true);
        });
      });

    this.bleService.onDisconnected()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.ngZone.run(() => {
          this.iqDevice.isConnected = false;
          this.onConnected.emit(false);
          this.onDisplayBoardFirmwareVersion.emit(null);
          this.onMainBoardFirmwareVersion.emit(null);
        });
      });

    this.bleService.onForce()
      .pipe(takeUntil(this.destroy$))
      .subscribe(force => {
        this.ngZone.run(() => {
          if (force != null) {
            this.iqDevice.force = force;
            this.onForceLevel.emit(force);

            this.iqDevice.forceValue = this.bleService.getForceValueForLevel(force);
          }
        });
      });
    this.bleService.onPreload()
      .pipe(takeUntil(this.destroy$))
      .subscribe(preload => {
        this.ngZone.run(() => {
          this.iqDevice.preload = preload;
          this.onPreload.emit(preload);
        });
      });

    this.bleService.onSerialNumber()
      .pipe(takeUntil(this.destroy$))
      .subscribe(serial => {
        this.ngZone.run(() => {
          this.iqDevice.serialNumber = serial;
          this.onSerialNumber.emit(serial);
        });
      });

    this.bleService.onDisplayBoardFirmwareVersion()
      .pipe(takeUntil(this.destroy$))
      .subscribe(version => {
        this.ngZone.run(() => {
          this.onDisplayBoardFirmwareVersion.emit(version);
        });
      });

    this.bleService.onMainBoardFirmwareVersion()
      .pipe(takeUntil(this.destroy$))
      .subscribe(version => {
        this.ngZone.run(() => {
          this.onMainBoardFirmwareVersion.emit(version);
        });
      });

    this.bleService.onTriggerState()
      .pipe(takeUntil(this.destroy$))
      .subscribe(trigger => {
        this.ngZone.run(() => {
          this.iqDevice.trigger = trigger;
          this.onTrigger.emit(trigger);
        });
      });

    this.bleService.onDoubleTrigger()
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.ngZone.run(() => {
          this.onDoubleTrigger.emit();
        });
      });

    this.bleService.onSessionLog()
      .pipe(takeUntil(this.destroy$))
      .subscribe(logItem => {
        this.ngZone.run(() => {
          if (logItem) {
            this.iqDevice.temp = logItem.solenoidTemp;
            this.iqDevice.thrusts = logItem.thrusts;
            this.iqDevice.triggers = logItem.triggers;

            this.onDeviceInformation.emit(logItem);
          }
        });
      });

    this.bleService.onDebug()
      .pipe(takeUntil(this.destroy$))
      .subscribe(data => {
        this.ngZone.run(() => {
          if (data) {
            this.displayOutput(data.message, data.optionalParams);
          }
        });
      });

    this.bleService.onError()
      .pipe(takeUntil(this.destroy$))
      .subscribe(error => {
        this.ngZone.run(() => {
          if (error) {
            this.displayOutput('Error:', error.message, error.optionalParams);
          }
        });
      });


    this.bleService.onFrequency()
      .pipe(
        throttleTime(50, asyncScheduler, { leading: true, trailing: true }),
        takeUntil(this.destroy$)
      )
      .subscribe(frequencies => {
        this.ngZone.run(() => {
          if (this.iqDevice.isConnected && frequencies && frequencies.length) {
            this.frequency = frequencies[frequencies.length - 1];
            this.onFrequency.emit(frequencies);
          }
        });
      });

    this.bleService.onTreatment()
      .pipe(takeUntil(this.destroy$))
      .subscribe(treatment => {
        this.ngZone.run(() => {
          if (treatment) this.onTreatment.emit(treatment);
        });
      });

    await this.bleService.initializeBLE();
    this.bleReady = true;
  }

  displayOutput(message: any, ...optionalParams: any[]) {
    const firstParam = optionalParams.length > 0 ? optionalParams[0] : '';

    // Add new message at the top, including the first optionalParam as a string
    this.debugMessages.unshift(`${message} ${firstParam}`);

    // Keep only the latest 10 messages
    if (this.debugMessages.length > 10) {
      this.debugMessages.pop(); // Remove the last (oldest) message
    }

  }

  async connect() {
    // Always allow manual connection - scanOnStartup only controls auto-connect on init
    if (this.connecting)
      return;

    if (this.iqDevice.isConnected) {
      return;
    }

    if (this.scanning) {
      return;
    }

    this.connecting = true;

    setTimeout(async () => {
      try {
        await this.bleService.startScanning(null); // Start scanning on button click
        this.connecting = false;
      } catch (error) {
        this.connecting = false;
      }
    }, 1);
  }

  async disconnect() : Promise<void> {
    this.connecting = false;
    return await this.bleService.disconnect();
  }

  async WriteSerialNumber(serialNumer: string) {
    if (this.iqDevice.isConnected) {
      await this.bleService.writeSerialNumber(serialNumer);
    }
  }

  toggleConnection() {
    if (this.iqDevice.isConnected) {
      this.disconnect();
    } else {
      this.connect();
    }
  }


}

